<script setup lang="ts">
import { useLandingStore } from "@/modules/landing-module";
import { computed, onBeforeMount, ref, watch } from "vue";
import { UiSkeleton } from "@packages/component-library";
import { useInterval } from "@vueuse/core";
import { useI18n } from "vue-i18n";

const { t } = useI18n();
const landingStore = useLandingStore();
const coupons = computed(() => landingStore.coupons);
const currentSlide = ref(0);
const sliderTransition = computed(() => currentSlide.value * -76);

const { counter, reset, pause, resume } = useInterval(1000, { controls: true });

watch(currentSlide, (newValue) => {
  if (newValue === coupons.value.length) {
    currentSlide.value = 0;
    reset();
  }
});

watch(counter, (newValue) => {
  if (newValue === 3) {
    currentSlide.value++;
    reset();
  }
});

watch(coupons, (newValue) => {
  if (newValue) {
    resume();
  }
});

onBeforeMount(() => {
  pause();

  if (coupons.value.length) {
    resume();
  }
});
</script>

<template>
  <RouterLink :to="{ name: 'CouponsView' }" class="banner-coupons">
    <div class="banner-coupons__slider">
      <div
        v-if="coupons.length"
        class="coupons-slider"
        :style="{ transform: `translateX(${sliderTransition}px)` }"
      >
        <div
          v-for="(coupon, index) in coupons"
          :key="index"
          :class="{ active: index === currentSlide + 1 }"
          :style="{ backgroundImage: `url(${coupon.image_src})` }"
        />
        <div
          v-for="(coupon, index) in coupons"
          :key="index"
          :style="{ backgroundImage: `url(${coupon.image_src})` }"
        />
      </div>

      <template v-else>
        <UiSkeleton v-for="item in 6" :key="item" type="avatar" />
      </template>
    </div>
    <div class="banner-coupons__info">
      <h5>{{ t("home.coupons.title") }}</h5>
      <p>{{ t("home.coupons.subTitle") }}</p>
    </div>
  </RouterLink>
</template>

<style scoped lang="scss">
.banner-coupons {
  width: 100%;
  border-radius: 24px;
  overflow: hidden;
  background-color: var(--color-semantic-background-normal-bg-secondary);

  &__info {
    padding: 0 calc($base-space * 6) calc($base-space * 6);

    > h5 {
      @include body-bold-l();
      color: var(--color-semantic-content-normal-primary);
    }

    > p {
      margin: calc($base-space * 2) 0 0;
      @include body-s();
      color: var(--color-semantic-content-normal-secondary);
    }
  }

  &__slider {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: calc($base-space * 4);
    position: relative;
    height: 60px;
    margin: calc($base-space * 8) 0;

    > .ui-skeleton {
      width: 60px;
      height: 60px;
      flex-shrink: 0;
    }
  }
}

.coupons-slider {
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  gap: calc($base-space * 4);
  position: absolute;
  left: -30px;
  transition: all 0.6s ease-out;

  > div {
    width: 60px;
    height: 60px;
    border-radius: 60px;
    border: 1px solid var(--color-semantic-content-normal-contrast-primary);
    flex-shrink: 0;
    @include centerBackgroundImageCover();
    transition: all 0.3s ease-out;

    &.active {
      transform: scale(1.2);
    }
  }
}
</style>
